<template>
  <div v-if="!!uploadUrl">
    <v-container>
      <v-row>
        <v-col>
          <field-label
            label="Utilize o QRCode para subir fotos diretamente do celular"
            :class="{ 'd-flex flex-column align-center text-center': $vuetify.breakpoint.smAndDown }"
          >
            <qrcode-vue class="mt-4" :value="uploadUrl" level="H" :size="196" />
          </field-label>
        </v-col>
        <v-col>
          <field-label
            label="Compartilhamento de link com o paciente para envio de imagens"
            :class="{ 'text-center': $vuetify.breakpoint.smAndDown }"
          >
            <v-row>
              <v-col class="text-center">
                <v-btn x-large icon target="_blank" color="primary" @click="copyToClipboard"
                  ><v-icon x-large>mdi-link</v-icon></v-btn
                >
                <p class="mb-0">Copiar link</p>
              </v-col>
              <v-col class="text-center">
                <v-btn x-large icon :disabled="!patientMobile" :href="whatsappLink" target="_blank" color="primary"
                  ><v-icon x-large>mdi-whatsapp</v-icon></v-btn
                >
                <p class="mb-0">Compartilhar<br />por WhatsApp<br />{{ patientMobile }}</p>
              </v-col>
            </v-row>
            <v-row>
              <v-col class="text-center">
                <v-btn
                  x-large
                  icon
                  :disabled="!patientMobile || smsCooldown"
                  target="_blank"
                  color="primary"
                  @click="sendSms"
                  ><v-icon x-large>mdi-message-processing</v-icon></v-btn
                >
                <p class="mb-0">Enviar por SMS<br />{{ patientMobile }}</p>
                <small v-if="smsCooldown">Aguarde {{ resendSmsSeconds }}s para poder enviar novamente</small>
              </v-col>
              <v-col class="text-center">
                <template v-if="false">
                  <v-btn
                    x-large
                    icon
                    :disabled="!patientEmail"
                    :href="`mailto:${patientEmail}`"
                    target="_blank"
                    color="primary"
                    ><v-icon x-large>mdi-email</v-icon></v-btn
                  >
                  <p class="mb-0">Enviar por Email<br />{{ patientEmail }}</p>
                </template>
              </v-col>
            </v-row>
          </field-label>
        </v-col>
      </v-row>
      <v-row>
        <v-col :style="{ 'flex-basis': $vuetify.breakpoint.smAndDown ? 'auto' : 0 }">
          <field-label
            label="Utilize o campo abaixo para subir arquivos a partir deste computador"
            :class="{ 'd-flex flex-column align-center text-center': $vuetify.breakpoint.smAndDown }"
          >
            <attendance-file-uploader
              :style="{ width: $vuetify.breakpoint.smAndDown ? '100%' : 'auto' }"
              class="mt-2"
              :attendanceId="attendanceId"
              :uuid="uuid"
              accept=".dcm,.dicom,application/dicom,image/*,application/zip,application/msword,application/pdf"
              :errors.sync="fileErrors"
              :uploaded.sync="uploaded"
              hide-errors
              hide-uploaded
              @send:finished="
                $event.uploaded === $event.total
                  ? $root.ok('Arquivos enviados com sucesso!')
                  : $root.error('Um ou mais arquivos não puderam ser enviados!')
              "
            />
          </field-label>
        </v-col>
        <v-col>
          <div v-if="fileErrors.length > 0">
            <strong class="red--text">Atenção</strong>
            <p class="mb-2">Um erro impediu o envio dos arquivos abaixo. Tente fazer o upload novamente.</p>
            <p class="mb-0">{{ fileErrors.join(', ') }}</p>
          </div>
        </v-col>
      </v-row>
      <v-row>
        <v-col :style="{ 'flex-basis': $vuetify.breakpoint.smAndDown ? 'auto' : 0 }">
          <div v-if="loadingFiles" class="d-flex flex-column fill-height gap-2 justify-center align-center">
            <v-progress-circular indeterminate />
            <p>Carregando imagens do atendimento...</p>
          </div>
          <field-label
            v-else
            :label="`${uploaded.length} arquivo(s) encontrados para este atendimento`"
            :titleClass="$vuetify.breakpoint.smAndDown ? 'd-flex flex-column align-center text-center flex-wrap' : ''"
          >
            <template #label-extra>
              <v-btn small outlined color="primary" @click="loadAttendanceFiles"
                >recarregar arquivos <v-icon small>mdi-refresh</v-icon></v-btn
              >
              <v-spacer v-if="$vuetify.breakpoint.mdAndUp" />
            </template>
            <v-sheet class="d-flex gap-4 flex-wrap overflow-y-auto mt-2">
              <div class="p-relative" v-for="document in uploaded" :key="`document-${document.id}`">
                <v-card
                  class="thumb cursor-pointer align-center"
                  v-if="isPhoto(document)"
                  @click="showImage(document)"
                  max-width="84"
                  height="86"
                >
                  <v-img :src="document.url" max-width="84" height="84" :title="document.name" />
                  <p class="ma-0 align-center font-weight-medium">
                    {{ $util.toFormattedDate(document.referenceDate) }}
                  </p>
                </v-card>
                <download-frame v-else :url="document.url" :name="document.name">
                  <v-sheet
                    class="thumb cursor-pointer d-flex gap-1 flex-column justify-center align-center"
                    width="84"
                    height="84"
                    :title="document.name"
                  >
                    <v-icon x-large>mdi-download</v-icon>
                    <p class="ma-0 text-body-2 font-weight-medium">EXAME</p>
                  </v-sheet>
                  <p class="ma-0 align-center font-weight-medium">
                    {{ $util.toFormattedDate(document.referenceDate) }}
                  </p>
                </download-frame>

                <v-btn
                  class="white p-absolute top-1 right-1"
                  x-small
                  icon
                  title="Remover arquivo"
                  color="error"
                  @click="removeFile(document)"
                  ><v-icon small>mdi-delete</v-icon></v-btn
                >
              </div>
            </v-sheet>
          </field-label>
        </v-col>
      </v-row>
    </v-container>
    <ReferenceDocumentDialog @document-deleted="documentDeleted" ref="documentDialog"></ReferenceDocumentDialog>
  </div>
  <v-sheet v-else>
    <h1>
      Este atendimento foi gerado sem a possibilidade de enviar imagens.
      <br />
      Se você acredita que isto é um erro, entre em contato com o Suporte.
    </h1>
  </v-sheet>
</template>
<script>
import BasePhase from '@/components/AttendanceForm/general/phase/BasePhase.vue'
import FieldLabel from '@/components/FieldLabel.vue'
import AttendanceFileUploader from '../../AttendanceFileUploader.vue'
import ReferenceDocumentDialog from '../../ReferenceDocumentDialog.vue'
import DownloadFrame from '@/components/DownloadFrame.vue'
import QrcodeVue from 'qrcode.vue'

import { createNamespacedHelpers } from 'vuex'
const { mapState: attendanceStateMap, mapGetters: attendanceGettersMap } = createNamespacedHelpers('attendance')

import { DOCUMENT_PHOTO } from '@/constants/attendance'

export default {
  components: {
    FieldLabel,
    AttendanceFileUploader,
    DownloadFrame,
    QrcodeVue,
    ReferenceDocumentDialog,
  },
  extends: BasePhase,
  computed: {
    ...attendanceStateMap(['attendance']),
    ...attendanceGettersMap(['patientMobile', 'patientEmail']),
    uuid() {
      return this.attendance.patient.uuid
    },
    patientId() {
      return this.attendance.patient.id
    },
    uploadUuid() {
      return this.attendance?.uploadPhotoUuid
    },
    attendanceId() {
      return parseInt(this.attendance.id)
    },
    uploadUrl() {
      if (!this.uploadUuid) {
        return null
      }

      return `${window.location.origin}/upload-attendance-photo?u=${this.uuid}&a=${this.attendanceId}`
    },
    whatsappLink() {
      if (!this.uploadUrl || !this.patientMobile) {
        return null
      }

      const mobileNumber = this.$util.toNumbersOnly(this.patientMobile)
      const message = this.$util.urlEncode(`Envie fotos ao seu médico em ${this.uploadUrl}`)

      return `https://api.whatsapp.com/send?phone=55${mobileNumber}&text=${message}&lang=pt_br`
    },
  },
  data() {
    return {
      fileErrors: [],
      uploaded: [],
      loadingFiles: false,
      smsCooldown: false,
      resendSmsSeconds: 60,
    }
  },
  methods: {
    getDefaultData() {
      return null
    },
    copyToClipboard() {
      this.$util.copyToClipboard(this.uploadUrl)
      this.$root.info('Link copiado para a área de transferência')
    },
    showImage(document) {
      this.$refs.documentDialog.openDialog(document)
    },
    isPhoto(document) {
      return document.type === DOCUMENT_PHOTO
    },
    async loadAttendanceFiles() {
      if (!this.attendanceId) {
        return
      }

      try {
        this.loadingFiles = true
        const result = await this.$api.getAttendanceDocumentFiles(this.attendance.id, {
          sortBy: ['id'],
          sortDesc: [true],
        })
        this.uploaded = result.items
      } catch (error) {
        this.$root.error(error)
      } finally {
        this.loadingFiles = false
      }
    },
    documentDeleted() {
      this.loadAttendanceFiles()
    },
    async removeFile(document) {
      if (!(await this.$root.confirm('Tem certeza de que deseja remover esta imagem?'))) {
        return
      }

      try {
        await this.$api.removePatientAttendanceFile(document.id)
        this.loadAttendanceFiles()
        this.$root.ok('Imagem removida com sucesso!')
      } catch (error) {
        this.$root.error(error)
      }
    },
    async sendSms() {
      try {
        this.smsCooldown = true
        await this.$api.sendSmsUploadPhoto(this.attendanceId)
        setTimeout(() => (this.smsCooldown = false), this.resendSmsSeconds * 1000)
      } catch (error) {
        this.smsCooldown = false
        this.$root.error(error)
      }
    },
  },
  mounted() {
    this.loadAttendanceFiles()
  },
}
</script>

<style scoped>
.thumb {
  border: 1px dotted lightgray;
}
</style>
